import React, { useEffect, useRef, useState } from "react";
import cx from "classnames";
import { animated, useTransition } from "@react-spring/web";
import { Dropdown } from "react-bootstrap";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDndMonitor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis, restrictToWindowEdges } from "@dnd-kit/modifiers";

import Actions from "src/components/actionbar";
import Button from "src/components/button";
import SortByAction from "src/components/sortByAction";
import TruncateWithTooltip from "src/components/truncateWithTooltip";
import DeleteFormField from "../deleteFormField";

import { guid } from "src/utils/util";
import { CustomFormField, FormFieldOption, FormFieldType } from "src/types/settings/customFormFields";
import { SortOrder } from "src/types/common";

import styles from "./styles.module.scss";

interface DetailViewProps {
  field: CustomFormField;
  onChange: (updated: CustomFormField) => void;
  onDelete: () => void;
  showDeleteSlider: boolean;
  onCloseDelete: () => void;
  onConfirmDelete: () => void;
  isDeleting: boolean;
}

const LIST_SORT_OPTIONS = [
  { label: "Name",          value: "name"         },
  { label: "Modified date", value: "modifiedDate" },
  { label: "Created date",  value: "createdDate"  },
];

const FIELD_TYPE_LABEL: Record<FormFieldType, string> = {
  string_field: "Text",
  number_field: "Number",
  boolean_field: "Decision",
  list_field: "List",
};

const INITIAL_STATE_OPTIONS = [
  { label: "Checked",   value: "checked" },
  { label: "Unchecked", value: "unchecked" },
];

// ── Outlined input ────────────────────────────────────────────────────────────
interface OutlinedInputProps {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  className?: string;
}

const OutlinedInput: React.FC<OutlinedInputProps> = ({
  label, value, placeholder, onChange, className,
}) => (
  <div className={cx(styles.fieldGroup, className)}>
    <label className={styles.fieldLabel}>{label}</label>
    <input
      className={styles.outlinedInput}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

// ── Outlined select (Initial state) ──────────────────────────────────────────
interface OutlinedSelectProps {
  label: string;
  value: string;
  options: { label: string; value: string }[];
  onChange: (value: string) => void;
  className?: string;
}

const OutlinedSelect: React.FC<OutlinedSelectProps> = ({
  label, value, options, onChange, className,
}) => {
  const selectedLabel = options.find((o) => o.value === value)?.label ?? options[0]?.label;

  return (
    <div className={cx(styles.fieldGroup, className)}>
      <label className={styles.fieldLabel}>{label}</label>
      <Dropdown>
        <Dropdown.Toggle as="div" className={styles.outlinedSelect}>
          <span>{selectedLabel}</span>
          <i className={cx("paperflite-dropdown", styles.selectCaret)} />
        </Dropdown.Toggle>
        <Dropdown.Menu
          className={styles.selectMenu}
          renderOnMount
        >
          {options.map((opt) => (
            <Dropdown.Item
              key={opt.value}
              className={cx(styles.selectItem, { [styles.activeSelectItem]: opt.value === value })}
              onClick={() => onChange(opt.value)}
            >
              {opt.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

// ── Detail view ───────────────────────────────────────────────────────────────
const DetailView: React.FC<DetailViewProps> = ({
  field,
  onChange,
  onDelete,
  showDeleteSlider,
  onCloseDelete,
  onConfirmDelete,
  isDeleting,
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const transition = useTransition(field, {
    keys: field?.id || "new",
    from: { opacity: 0, marginTop: 40 },
    enter: { opacity: 1, marginTop: 0 },
    config: { duration: 300 },
  });

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [isEditingName]);

  const handleChange = <K extends keyof CustomFormField>(key: K, value: CustomFormField[K]) => {
    onChange({ ...field, [key]: value });
  };

  return (
    <div className={styles.container}>
      {transition((style, f) =>
        f && (
          <animated.div style={style} className={styles.content}>{(() => {
            const isDecision = f.type === "boolean_field";
            const isList = f.type === "list_field";
            return (<>
            <div className={styles.nameRow}>
              {isEditingName ? (
                <input
                  ref={nameInputRef}
                  className={styles.nameInput}
                  value={f.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  onBlur={() => setIsEditingName(false)}
                  onKeyDown={(e) => e.key === "Enter" && setIsEditingName(false)}
                  placeholder="Enter field name"
                />
              ) : (
                <div className={styles.nameBold} onClick={() => setIsEditingName(true)}>
                  <TruncateWithTooltip
                    text={f.name || "Untitled"}
                    placement="top"
                  />
                </div>
              )}
              <div className={styles.nameRowRight}>
                {!!f.id && (
                  <Actions.IconButton
                    icon="paperflite-delete"
                    tooltip="Delete this field"
                    onClick={onDelete}
                  />
                )}
              </div>
            </div>

            {isDecision ? (
              <>
                <OutlinedInput
                  label="Instruction"
                  value={f.instructions ?? ""}
                  placeholder="e.g. Do you agree?"
                  onChange={(v) => handleChange("instructions", v)}
                />
                <div className={styles.splitRow}>
                  <OutlinedInput
                    label="Help text"
                    value={f.helpText ?? ""}
                    placeholder="Enter helper text"
                    onChange={(v) => handleChange("helpText", v)}
                    className={styles.splitField}
                  />
                  {/* initialState is boolean in API — bridge to "checked"/"unchecked" for display */}
                  <OutlinedSelect
                    label="Initial state"
                    value={f.initialState === false ? "unchecked" : "checked"}
                    options={INITIAL_STATE_OPTIONS}
                    onChange={(v) => handleChange("initialState", v === "checked")}
                    className={styles.splitField}
                  />
                </div>
              </>
            ) : (
              <>
                <OutlinedInput
                  label="Placeholder text"
                  value={f.placeholder ?? ""}
                  placeholder="e.g. Select..."
                  onChange={(v) => handleChange("placeholder", v)}
                />
                <OutlinedInput
                  label="Help text"
                  value={f.helpText ?? ""}
                  placeholder="Enter helper text"
                  onChange={(v) => handleChange("helpText", v)}
                />
              </>
            )}

            {/* ── List options (list type only) ── */}
            {isList && (
              <ListOptions
                options={f.options ?? []}
                onOptionsChange={(opts) => handleChange("options", opts)}
              />
            )}

            <DeleteFormField
              show={showDeleteSlider}
              onClose={onCloseDelete}
              selectedField={f}
              onConfirm={onConfirmDelete}
              isDeleting={isDeleting}
            />
          </>); })()}
          </animated.div>
        )
      )}
    </div>
  );
};

export default DetailView;


// ── List options ──────────────────────────────────────────────────────────────
interface ListOptionsProps {
  options: FormFieldOption[];
  onOptionsChange: (opts: FormFieldOption[]) => void;
}

const ListOptions: React.FC<ListOptionsProps> = ({ options, onOptionsChange }) => {
  const [sort, setSort] = useState<{ sortBy: string; orderBy: SortOrder }>({
    sortBy: "name",
    orderBy: SortOrder.ASC,
  });
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const activeItem = options.find((o) => o.id === activeId);

  // Apply sort directly to parent options so drag order and sort are both persisted
  const handleSortChange = (newSort: { sortBy: string; orderBy: SortOrder }) => {
    setSort(newSort);
    const sorted = [...options].sort((a, b) => {
      const cmp = a.value.localeCompare(b.value);
      return newSort.orderBy === SortOrder.ASC ? cmp : -cmp;
    });
    onOptionsChange(sorted);
  };

  const handleAddOption = () => {
    const newOpt: FormFieldOption = { id: guid(), value: `Option ${options.length + 1}` };
    onOptionsChange([...options, newOpt]);
  };

  const handleValueChange = (id: string, value: string) => {
    onOptionsChange(options.map((o) => (o.id === id ? { ...o, value } : o)));
  };

  const handleDelete = (id: string) => {
    onOptionsChange(options.filter((o) => o.id !== id));
  };

  return (
    <div className={styles.listOptions}>
      <div className={styles.listOptionsHeader}>
        <div className={styles.listOptionsTitle}>
          <span className={styles.listOptionsTitleText}>List options</span>
          <span className={styles.listOptionsSubtitle}>
            Add and arrange options for this field.
          </span>
        </div>
        <div className={styles.listOptionControls}>
          <SortByAction
            value={sort}
            onChange={handleSortChange}
            sortOptions={LIST_SORT_OPTIONS}
          />
          <Button
            className={styles.addOptionBtn}
            variant="secondary"
            icon="paperflite-add"
            text="Add options"
            onClick={handleAddOption}
          />
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
      >
        <ListReorderMonitor
          options={options}
          setOptions={onOptionsChange}
          setActiveId={setActiveId}
        />
        <SortableContext
          items={options.map((o) => o.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className={styles.optionsList}>
            {options.map((opt) => (
              <SortableOptionItem
                key={opt.id}
                option={opt}
                isDragging={activeId === opt.id}
                onChange={(val) => handleValueChange(opt.id, val)}
                onDelete={() => handleDelete(opt.id)}
              />
            ))}
          </div>
        </SortableContext>

        <DragOverlay>
          {activeItem && (
            <div className={cx(styles.optionRow, styles.draggingOverlay)}>
              <i className={cx("paperflite-drag", styles.dragHandle)} />
              <span className={styles.optionText}>{activeItem.value}</span>
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

// ── Reorder monitor ───────────────────────────────────────────────────────────
interface ListReorderMonitorProps {
  options: FormFieldOption[];
  setOptions: (opts: FormFieldOption[]) => void;
  setActiveId: (id: string | null) => void;
}

const ListReorderMonitor: React.FC<ListReorderMonitorProps> = ({
  options, setOptions, setActiveId,
}) => {
  useDndMonitor({
    onDragStart({ active }) { setActiveId(active.id as string); },
    onDragOver({ active, over }) {
      if (!over || active.id === over.id) return;
      const oldIndex = options.findIndex((o) => o.id === active.id);
      const newIndex = options.findIndex((o) => o.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        setOptions(arrayMove(options, oldIndex, newIndex));
      }
    },
    onDragEnd() { setActiveId(null); },
  });
  return null;
};

// ── Sortable option row ───────────────────────────────────────────────────────
interface SortableOptionItemProps {
  option: FormFieldOption;
  isDragging: boolean;
  onChange: (value: string) => void;
  onDelete: () => void;
}

const SortableOptionItem: React.FC<SortableOptionItemProps> = ({
  option, isDragging, onChange, onDelete,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: option.id });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      style={{
        transform: CSS.Translate.toString(transform),
        transition: isDragging ? "none" : transition,
      }}
      className={cx(styles.optionRow, { [styles.optionRowDragging]: isDragging })}
    >
      <div className={styles.dragHandleWrapper} {...listeners}>
        <i className={cx("paperflite-drag", styles.dragHandle)} />
      </div>
      <input
        className={styles.optionInput}
        value={option.value}
        placeholder="Option value"
        onChange={(e) => onChange(e.target.value)}
      />
      <button className={styles.deleteOptionBtn} onClick={onDelete}>
        <i className="paperflite-delete" />
      </button>
    </div>
  );
};
